import React from 'react';
import { Heart, Loader2 } from 'lucide-react';

interface LoadingFallbackProps {
  message?: string;
  fullScreen?: boolean;
  showProgress?: boolean;
  progress?: number;
  showTips?: boolean;
}

const LoadingFallback: React.FC<LoadingFallbackProps> = ({ 
  message = "Loading PharmPlus...", 
  fullScreen = true,
  showProgress = false,
  progress = 0,
  showTips = false
}) => {
  // Health tips to show during loading
  const healthTips = [
    "Stay hydrated! Aim for 8 glasses of water daily.",
    "A 30-minute walk can boost your mood and energy levels.",
    "Eating fruits and vegetables daily helps strengthen your immune system.",
    "Regular health check-ups can help detect issues early.",
    "Taking short breaks from screen time reduces eye strain.",
    "Deep breathing exercises can help reduce stress and anxiety.",
    "Getting 7-8 hours of sleep improves overall health and productivity.",
    "Regular handwashing is one of the best ways to prevent illness."
  ];
  
  // Randomly select a health tip
  const randomTip = healthTips[Math.floor(Math.random() * healthTips.length)];

  const content = (
    <div className="flex flex-col items-center justify-center p-8 animate-fade-in">
      <div className="relative mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
          <Heart className="text-white" size={32} />
        </div>
        <div className="absolute -bottom-2 -right-2">
          <Loader2 className="text-green-600 animate-spin" size={20} />
        </div>
      </div>
      
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{message}</h3>
      <p className="text-gray-600 text-center max-w-sm">
        Connecting you to Ghana's trusted health community...
      </p>
      
      {showProgress && (
        <div className="w-64 mt-6">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right">{progress}% complete</p>
        </div>
      )}
      
      {showTips && (
        <div className="mt-8 bg-blue-50 border border-blue-100 rounded-lg p-4 max-w-md">
          <p className="text-sm text-blue-800">
            <span className="font-medium">Health Tip:</span> {randomTip}
          </p>
        </div>
      )}
      
      <div className="mt-6 flex space-x-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 bg-green-500 rounded-full animate-bounce"
            style={{ animationDelay: `${i * 0.1}s` }}
          />
        ))}
      </div>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center">
        {content}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-12">
      {content}
    </div>
  );
};

export default LoadingFallback;